import { createContext, useContext, useEffect, useRef, useState, type ReactNode } from "react";
import { pick, useT } from "../i18n/index.js";
import { Button } from "../ui/Button.js";
import { useReducedMotion } from "../units/useReducedMotion.js";
import { CosmeticPreviewPool } from "./CosmeticPreviewPool.js";
import type { CosmeticView } from "./types.js";

const Previews = createContext<{ pool: CosmeticPreviewPool | null; error: boolean }>({
  pool: null,
  error: false,
});

export function CosmeticPreviewProvider({ children }: { children: ReactNode }) {
  const [value, setValue] = useState<{ pool: CosmeticPreviewPool | null; error: boolean }>({
    pool: null,
    error: false,
  });
  useEffect(() => {
    let pool: CosmeticPreviewPool;
    try {
      pool = new CosmeticPreviewPool();
      setValue({ pool, error: false });
    } catch {
      setValue({ pool: null, error: true });
      return;
    }
    return () => pool.dispose();
  }, []);
  return <Previews.Provider value={value}>{children}</Previews.Provider>;
}

export function CosmeticModelPreview({
  cosmetic,
  animate = false,
}: {
  cosmetic: CosmeticView;
  animate?: boolean;
}) {
  const t = useT();
  const { pool, error } = useContext(Previews);
  const canvas = useRef<HTMLCanvasElement>(null);
  const registration = useRef<ReturnType<CosmeticPreviewPool["register"]> | null>(null);
  const [state, setState] = useState<"loading" | "ready" | "error">("loading");
  const [attempt, setAttempt] = useState(0);
  const reduced = useReducedMotion();
  const reducedRef = useRef(reduced);
  reducedRef.current = reduced;
  useEffect(() => {
    if (!pool || !canvas.current) return;
    setState("loading");
    delete canvas.current.dataset["rendered"];
    const current = pool.register(canvas.current, cosmetic, animate, reducedRef.current, setState);
    registration.current = current;
    return () => {
      registration.current = null;
      current.remove();
    };
  }, [pool, cosmetic.id, cosmetic.asset, cosmetic.colour, animate, attempt]);
  useEffect(() => registration.current?.setReduced(reduced), [reduced]);
  return (
    <div className="cos-model-preview" data-state={error ? "error" : state}>
      <canvas
        ref={canvas}
        role="img"
        aria-label={pick(cosmetic.name)}
        className="cos-canvas"
        data-testid={`cos-preview-${cosmetic.id}`}
      />
      {error || state === "error" ? (
        <div className="cos-preview-status" role="status">
          <span>{t("store.cos.previewFailed")}</span>
          {!error ? (
            <Button variant="ghost" onClick={() => setAttempt((n) => n + 1)}>
              {t("store.retry")}
            </Button>
          ) : null}
        </div>
      ) : state === "loading" ? (
        <span className="cos-preview-status" role="status">
          {t("common.loading")}
        </span>
      ) : null}
    </div>
  );
}
